import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';

import { Loader } from '@calm-web/design-system';

import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';

import { useSimplifiedSignupContext } from '../Context';

// Dynamic import breaks the circular import chain
// ControlScreens → Goals → PreSignupFlow → BundledSimplifiedSignup → ControlFlow → ControlScreens.
const EmbeddedQuestionnaire = dynamic(() => import('./EmbeddedQuestionnaire'), { ssr: false });

const Goals = () => {
	const { routeToScreen } = useSimplifiedSignupContext();
	const { isEnrolled, isLoading } = useAmplitudeExperiment(
		AmplitudeExperimentNames.GOAL_QUESTIONNAIRE_POST_UPSELL,
	);
	const searchParams = useSearchParams();
	const routerPush = useRouterPush();
	const focus = searchParams?.get('focus');

	useEffect(() => {
		if (isLoading) return;
		if (!isEnrolled) {
			routeToScreen('handoff');
			return;
		}
		if (!focus) {
			routerPush('/signup-flow', { focus: 'goals' });
		}
	}, [isLoading, isEnrolled, focus, routeToScreen, routerPush]);

	// Refresh on the handoff URL re-renders SimplifiedSignup at its handoff screen
	// (see `signup-flow.tsx`'s page-level branch).
	const onSkipToEnd = useCallback(() => {
		routerPush('/signup-flow', { step: 'handoff' });
		routeToScreen('handoff');
	}, [routerPush, routeToScreen]);

	if (isLoading) {
		return <Loader />;
	}
	// Effect above will routeToScreen('handoff') / routerPush('?focus=goals') on the next tick.
	if (!isEnrolled || !focus) {
		return null;
	}

	return <EmbeddedQuestionnaire onSkipToEnd={onSkipToEnd} />;
};

export default Goals;
